import { getCurrentPermissions } from '@wix/ambassador-blog-v3-blog/http';
import { Scope, type Permissions } from '@wix/ambassador-blog-v3-blog/types';
import { getMember, getMyMember } from '@wix/ambassador-members-v1-member/http';
import { Set } from '@wix/ambassador-members-v1-member/types';
import { createAction } from '@wix/communities-blog-client-common';
import type { FeedPageThunkAction } from '@app/external/feed-page/types';
import { createPromisifiedAction } from '../../actions-promisifier/create-promisified-action';
import { blogAppDefId } from '../../constants/apps';
import { initializeInstanceValues } from '../instance-values/instance-values-actions';
import { config } from './permissions-checker-config';
import { type CurrentUser } from './types';

export const fetchCurrentUserRequest = createAction('currentUser/FETCH_REQUEST');
export const fetchCurrentUserSuccess = createAction(
  'currentUser/FETCH_SUCCESS',
  (payload: CurrentUser | undefined) => payload,
);
export const fetchCurrentUserFailure = createAction('currentUser/FETCH_FAILURE');

const getRolesForPermissionsChecker = (permissions: Permissions | undefined) => {
  const UNKNOWN_SCOPE = config[Scope.UNKNOWN_SCOPE];
  if (!permissions || !permissions.scope) {
    return UNKNOWN_SCOPE;
  }

  return config[permissions.scope] ?? UNKNOWN_SCOPE;
};

export const fetchCurrentUser =
  (tokenToPreviewPost?: string): FeedPageThunkAction<Promise<void>> =>
  async (dispatch, _, { httpClient, errorHandler, platformApi }) => {
    const { withErrorHandler, getResolvedError } = errorHandler;
    const userId = platformApi.user.currentUser.id;

    dispatch(fetchCurrentUserRequest());

    try {
      const instance = tokenToPreviewPost ?? platformApi.site.getAppToken?.(blogAppDefId);

      const signedInstance = tokenToPreviewPost
        ? { signedInstance: tokenToPreviewPost }
        : undefined;

      dispatch(initializeInstanceValues(instance));

      const permissionsPromise = withErrorHandler(
        () => httpClient.request(getCurrentPermissions({}), signedInstance),
        {
          errorCodesMap: {},
        },
      );

      const memberPromise = withErrorHandler(
        () =>
          httpClient.request(
            tokenToPreviewPost
              ? getMyMember({ fieldsets: [Set.EXTENDED] })
              : getMember({ id: userId, fieldsets: [Set.EXTENDED] }),
            signedInstance,
          ),
        {
          errorCodesMap: {},
        },
      );

      const [permissionsResponse, memberResponse] = await Promise.all([
        permissionsPromise,
        memberPromise,
      ]);

      const permissions = permissionsResponse.data.permissions;

      const user = {
        ...memberResponse.data.member,
        permissions,
        appStore: {
          [blogAppDefId]: {
            permissions: {
              role: getRolesForPermissionsChecker(permissions),
            },
          },
        },
      };

      dispatch(fetchCurrentUserSuccess(user));
    } catch (error) {
      dispatch(fetchCurrentUserFailure());
      getResolvedError(error);
    }
  };

export const fetchCurrentUserPromisified = createPromisifiedAction(
  fetchCurrentUser,
  (result) => result,
);
